import { Check } from '@untitledui/icons';
import { type ReactNode, useCallback } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Toggle } from '@/lib/base/forms/toggle';

/**
 * Shared presentational building blocks for email/calendar integration sections
 * (Gmail, Outlook, …). These own the visual design so every provider section
 * looks identical — provider-specific content/actions are passed in as props.
 */

interface IntegrationProviderHeaderProps {
  /** Provider brand mark (img or svg). */
  logo: ReactNode;
  /** Provider name, e.g. "Google Workspace". */
  title: string;
  /**
   * Which accounts this provider covers, e.g. "Gmail, Google Calendar and
   * Meet". Two provider cards listing near-identical capabilities are hard to
   * choose between; this is the line that tells a user which one is theirs.
   */
  subtitle?: string;
  /** Right-aligned action — a Connected badge or a Connect button. */
  action?: ReactNode;
}

export function IntegrationProviderHeader({
  logo,
  title,
  subtitle,
  action,
}: IntegrationProviderHeaderProps) {
  // items-center, not items-start: the logo is a fixed-height tile, so centring
  // it against the title block keeps it optically aligned whether or not the
  // provider has a subtitle. Must stay in sync with the hand-rolled header in
  // GmailIntegration, which is laid out identically.
  return (
    <div className='flex items-center justify-between gap-4'>
      <div className='flex min-w-0 items-center gap-3'>
        {logo}
        <div className='min-w-0'>
          <h3 className='font-semibold text-fg-primary'>{title}</h3>
          {subtitle && (
            <p className='mt-0.5 text-sm text-fg-tertiary'>{subtitle}</p>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

export interface IntegrationBenefit {
  /** Optional bold lead-in, e.g. "Gmail:". */
  label?: string;
  /** Benefit description. */
  text: string;
}

interface IntegrationBenefitsCardProps {
  items: IntegrationBenefit[];
}

export function IntegrationBenefitsCard({
  items,
}: IntegrationBenefitsCardProps) {
  return (
    <div className='mt-4 border-t border-border-secondary pt-4'>
      <ul className='grid gap-x-6 gap-y-1.5 sm:grid-cols-2'>
        {items.map(item => (
          <li
            key={item.label ?? item.text}
            className='flex items-start gap-2 text-sm'
          >
            <Check
              className='mt-0.5 size-4 shrink-0 text-fg-quaternary'
              aria-hidden='true'
            />
            <span className='text-fg-secondary'>
              {item.label && (
                <span className='font-medium text-fg-primary'>
                  {item.label}{' '}
                </span>
              )}
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Privacy reassurance for connecting a mailbox/calendar.
 *
 * Rendered ONCE per settings section, never per provider — the statement is
 * identical for every provider, so repeating it verbatim under each one was
 * pure duplication that made the page feel padded. Styled as a quiet footnote
 * rather than a filled callout: it is reassurance, not a warning, and it should
 * not out-shout the Connect buttons above it.
 */
export function IntegrationPrivacyNote() {
  return (
    <p className='text-xs text-fg-tertiary'>
      Your data is only accessed when explicitly requested during conversations.
      Access tokens are stored securely and can be revoked at any time.
    </p>
  );
}

interface IntegrationAccountCardProps {
  /** Provider brand mark (img or svg). */
  logo: ReactNode;
  /** Connected account email (may be null if the provider didn't return one). */
  email: string | null;
  /** Status badges shown next to the email (Connected, Primary, Expired, …). */
  badges?: ReactNode;
  /** Secondary line under the email, e.g. "Connected account". */
  subLabel?: string;
  /** Right-aligned action buttons (Disconnect, Reconnect, …). */
  actions?: ReactNode;
  /** Full-width content rendered below the header row (import status, toggles). */
  children?: ReactNode;
}

export function IntegrationAccountCard({
  logo,
  email,
  badges,
  subLabel,
  actions,
  children,
}: IntegrationAccountCardProps) {
  return (
    <div className='bg-bg-secondary rounded-lg border border-border-secondary overflow-hidden'>
      <div className='flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex min-w-0 items-center gap-3'>
          {logo}
          <div className='min-w-0'>
            <div className='flex flex-wrap items-center gap-2'>
              {email ? (
                <span className='truncate font-medium text-primary'>
                  {email}
                </span>
              ) : (
                <span className='truncate font-medium italic text-tertiary'>
                  Email unavailable
                </span>
              )}
              {badges}
            </div>
            {subLabel && <p className='text-xs text-tertiary'>{subLabel}</p>}
          </div>
        </div>
        {actions && (
          <div className='flex shrink-0 flex-wrap items-center justify-end gap-2'>
            {actions}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

interface IntegrationVisibilityToggleProps {
  title: string;
  description: string;
  entityId: string;
  /** Current visibility ("org" = shared with team, anything else = private). */
  currentVisibility: string | undefined;
  isUpdating: boolean;
  onToggle: (entityId: string, currentValue: boolean) => void;
}

/**
 * "Make X public to team by default" toggle row, rendered below an account card.
 * Provider-agnostic — operates purely on an integration entity id + visibility.
 */
export function IntegrationVisibilityToggle({
  title,
  description,
  entityId,
  currentVisibility,
  isUpdating,
  onToggle,
}: IntegrationVisibilityToggleProps) {
  const isOrg = currentVisibility === 'org';
  const handleChange = useCallback(() => {
    onToggle(entityId, isOrg);
  }, [onToggle, entityId, isOrg]);

  return (
    <div className='border-t border-border-secondary px-4 py-3'>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1'>
          <p className='text-sm font-medium text-primary'>{title}</p>
          <p className='text-xs text-tertiary mt-0.5'>{description}</p>
        </div>
        <Toggle
          isSelected={isOrg}
          onChange={handleChange}
          isDisabled={isUpdating}
          size='sm'
        />
      </div>
    </div>
  );
}

/** Convenience: a "Connected" success badge used in provider headers. */
export function ConnectedBadge() {
  return (
    <Badge type='color' size='sm' color='success'>
      Connected
    </Badge>
  );
}
